/**
 * Os preços da clínica (#1084): o preço particular de cada consulta (por
 * médico), exame e procedimento, copiado do sistema da clínica para o
 * atendente informar o paciente.
 *
 * O CRM não cadastra preço: a lista vem pelas operações do mapa da agenda
 * (`precos_de_consulta` e `precos_de_procedimentos`, ADR-0028), na rotina da
 * madrugada ou no botão "Atualizar preços agora".
 */

import { z } from 'zod';

/** Micros na moeda: `550_000_000` = R$ 550,00. O mesmo formato do valor da oportunidade. */
const ValorSchema = z.object({
  quantia_micros: z.number().int().nonnegative(),
  moeda: z.string().length(3),
});

export const TipoDePrecoSchema = z.enum(['consulta', 'exame', 'procedimento']);
export type TipoDePrecoDaApi = z.infer<typeof TipoDePrecoSchema>;

export const PrecoDaClinicaSchema = z.object({
  id: z.string().uuid(),
  tipo: TipoDePrecoSchema,
  codigo_procedimento: z.string(),
  nome: z.string(),
  grupo: z.string().nullable(),
  /** Só na consulta: o médico do preço. `null` no padrão da clínica e no exame. */
  medico: z.object({ id: z.string().uuid(), nome: z.string() }).nullable(),
  /** O preço particular de tabela. */
  preco: ValorSchema,
});
export type PrecoDaClinica = z.infer<typeof PrecoDaClinicaSchema>;

export const ListaDePrecosSchema = z.object({
  /** Os ativos da página, por tipo e nome; até `por_pagina`. */
  itens: z.array(PrecoDaClinicaSchema),
  /** Quantos casam com o filtro, em todas as páginas. */
  total: z.number().int().nonnegative(),
  /** A página devolvida, a partir de 1. */
  pagina: z.number().int().min(1),
  /** Quantos cabem numa página (hoje, 20). */
  por_pagina: z.number().int().min(1),
  /** Quando a lista foi conferida com o sistema da clínica pela última vez; `null` se nunca. */
  sincronizado_em: z.string().datetime({ offset: true }).nullable(),
});
export type ListaDePrecos = z.infer<typeof ListaDePrecosSchema>;

/**
 * O preço da consulta com um médico (#1100): o dele, ou o padrão da clínica
 * quando ele não tem preço próprio. `preco: null` quando não há nenhum dos dois.
 */
export const PrecoDaConsultaDoMedicoSchema = z.object({
  preco: ValorSchema.nullable(),
  /** De onde veio: `medico` (o preço dele) ou `padrao` (o da clínica). `null` sem preço. */
  origem: z.enum(['medico', 'padrao']).nullable(),
});
export type PrecoDaConsultaDoMedico = z.infer<typeof PrecoDaConsultaDoMedicoSchema>;

const ContagemSchema = z.object({
  criados: z.number().int().min(0),
  atualizados: z.number().int().min(0),
  inativados: z.number().int().min(0),
});

/** O que a sincronização fez em cada grupo; `null` no grupo que a organização não ligou. Sem nomes. */
export const ResultadoDaSincronizacaoDePrecosSchema = z.object({
  consultas: ContagemSchema.nullable(),
  procedimentos: ContagemSchema.nullable(),
});
export type ResultadoDaSincronizacaoDePrecos = z.infer<
  typeof ResultadoDaSincronizacaoDePrecosSchema
>;
